/**
 * Copyright © 2026 Adnuntius AS.
 */
import {ChangeDetectionStrategy, Component, DestroyRef, Inject, Input, NgZone, OnInit, inject} from '@angular/core';
import {firstValueFrom} from 'rxjs';
import * as _ from 'lodash';
import template from './marketplace-product-list-page.component.html';

import {SignalAdminListColumn, SignalAdminResourceListStateFactory} from '../../common/controller/signal-admin-resource-list-state';
import {Uuid} from '../../../components/util/uuid';
import {TeamApiService} from '../team/team-api.service';
import {LocalUserPermissions} from '../../../components/session/local-user-permissions';
import {NetworkDataApiService} from '../network/network-data-api.service';
import {MarketplaceProductApiService} from './marketplace-product-api.service';
import {FormsModule} from '@angular/forms';
import {TranslateModule} from '@ngx-translate/core';
import {UIRouterModule} from '@uirouter/angular';
import {ButtonsModule} from 'ngx-bootstrap/buttons';
import {AppSharedModule} from '../../common/app-shared.module';
import {AdminResourcePagesModule} from '../../common/controller/admin-resource-pages.module';
import {PercentageBarComponent} from '../../common/controller/progress-bar-percentage-component';
import {VelocityComponent} from '../../common/controller/velocity-component';
import {RateLimitComponent} from '../../common/controller/rate-limit.component';
import {AdminListModule} from '../../common/directives/admin-list.module';
import {FormControlsModule} from '../../common/directives/form-controls/form-controls.module';
import {LayoutComponentSummaryComponent} from '../../common/layout-component-summary-component';
import {TargetingModule} from '../../targeting/targeting.module';

@Component({
  standalone: true,
  imports: [
    AppSharedModule,
    FormsModule,
    TranslateModule,
    UIRouterModule,
    ButtonsModule,
    AdminListModule,
    AdminResourcePagesModule,
    PercentageBarComponent,
    VelocityComponent,
    RateLimitComponent,
    FormControlsModule,
    LayoutComponentSummaryComponent,
    TargetingModule
  ],
  selector: 'adn-marketplace-product-list-page',
  template,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MarketplaceProductListPageComponent implements OnInit {
  @Input() readOnly = false;

  readonly itemStateName = 'app.adm.marketplace-products.marketplace-product';

  readonly columns: SignalAdminListColumn[] = [
    {param: 'name'},
    {param: '', label: 'owner', sortable: false},
    {param: '', label: 'targeting', sortable: false},
    {param: 'layouts', label: 'layouts', sortable: false}
  ];

  canManageSystem = false;
  noTeams = false;
  isSetUp = false;
  editsMade = false;
  productSelectionPass = false;
  productSelectionError = false;

  productInfo: {[id: string]: any[]} = {};
  teamInfo: {[id: string]: any} = {};
  networkData: any;

  private previousNetworkData: any;
  private productQuery: Promise<void>;
  private teamQuery: Promise<void>;
  readonly list = inject(SignalAdminResourceListStateFactory).create(
    inject(MarketplaceProductApiService),
    {listId: 'fullMarketplaceProductList', baseTable: 'axProduct'},
    inject(DestroyRef)
  );

  constructor(
    private readonly marketplaceProductApi: MarketplaceProductApiService,
    private readonly networkDataApi: NetworkDataApiService,
    private readonly teamApi: TeamApiService,
    @Inject(LocalUserPermissions) private LocalUserPermissions: any,
    private ngZone: NgZone
  ) {}

  get teamInfoArray(): any[] {
    return _.sortBy(_.values(this.teamInfo), 'name');
  }

  ngOnInit(): void {
    this.list.readOnly.set(this.readOnly);
    this.list.load();

    this.canManageSystem = this.LocalUserPermissions.getAllNetworkPermissions().MANAGE_SYSTEM === true;

    this.productQuery = firstValueFrom(this.marketplaceProductApi.listOptions({pageSize: 501})).then((page: any) => {
      this.ngZone.run(() => {
        this.productInfo = {};
        _.forEach(page.results, (item: any) => {
          _.forEach(item.teams, (team: any) => {
            this.productInfo[team.id] = this.productInfo[team.id] || [];
            this.productInfo[team.id].push({
              id: item.id,
              name: item.name
            });
          });
        });
      });
    });

    this.teamQuery = firstValueFrom(this.teamApi.listOptions({
      pageSize: 501,
      filterBy: 'type',
      filterByValue: 'AX_ADVERTISER'
    })).then((page) => {
      this.ngZone.run(() => {
        this.teamInfo = {};
        _.forEach(page.results, (item: any) => {
          this.teamInfo[item.id] = {
            id: item.id,
            name: item.name
          };
        });
        this.noTeams = !page.results.length;
      });
    });

    Promise.all([this.productQuery, this.teamQuery]).then(() => {
      this.ngZone.run(() => {
        this.cleanData();
        this.networkDataApi.listOptions({filterByValue: 'defaultMarketplaceProducts', filterBy: 'type'}).subscribe((page: any) => {
          this.ngZone.run(() => {
            if (page.results.length) {
              const entry = page.results[0];
              try {
              this.networkData = _.cloneDeep(entry);
              } catch {
                this.networkData = null;
              }
            }

            this.cleanData();
            this.previousNetworkData = _.cloneDeep(this.networkData);
            this.isSetUp = true;
          });
        });
      });
    });
  }

  edit(): void {
    this.editsMade = true;
  }

  saveProductDefaults(): void {
    this.editsMade = false;
    this.productSelectionPass = false;
    this.productSelectionError = false;
    this.cleanData();
    firstValueFrom(this.networkDataApi.save(this.networkData)).then(() => {
      this.ngZone.run(() => {
        this.productSelectionPass = true;
        this.previousNetworkData = _.cloneDeep(this.networkData);
      });
    }).catch(() => {
      this.ngZone.run(() => {
        this.productSelectionError = true;
      });
    });
  }

  cancelProductDefaults(): void {
    this.networkData = _.cloneDeep(this.previousNetworkData);
    this.editsMade = false;
    this.productSelectionPass = false;
    this.productSelectionError = false;
  }

  productOptionsForTeam(teamId: string): any[] {
    return this.productInfo[teamId] || [];
  }

  trackById(_: number, item: any): string {
    return item.id;
  }

  private cleanData(): void {
    this.networkData = this.networkData || {
      id: Uuid.generate(),
      name: 'Default Marketplace Products',
      type: 'defaultMarketplaceProducts'
    };
    this.networkData.data = this.networkData.data || {};
    this.networkData.data.teamToProduct = this.normalizeTeamToProduct(this.networkData.data.teamToProduct);

    _.forEach(this.teamInfo, (team: any) => {
      if (!Object.prototype.hasOwnProperty.call(this.networkData.data.teamToProduct, team.id)) {
        this.networkData.data.teamToProduct[team.id] = null;
      }
    });

    const teamsToDelete: string[] = [];
    _.forEach(this.networkData.data.teamToProduct, (_value: any, key: string) => {
      if (!this.teamInfo[key]) {
        teamsToDelete.push(key);
      }
    });

    _.forEach(teamsToDelete, (key: string) => {
      delete this.networkData.data.teamToProduct[key];
    });
  }

  private normalizeTeamToProduct(teamToProduct: any): {[id: string]: string | null} {
    if (_.isString(teamToProduct)) {
      try {
        return JSON.parse(teamToProduct);
      } catch {
        return {};
      }
    }

    return _.isPlainObject(teamToProduct) ? teamToProduct : {};
  }
}
